import type { SelectOption } from "@amtana/design-system";
import type { Catalogs, Formats, PlainKey, Translate, Translation } from "@amtana/i18n/catalog";
import type { DomainStatus, Provider, SsoConnection } from "@amtana/sso-contract";

// Every word of the single sign-on screens: the admin's setup, its toasts and
// the page that stands in for the company's identity provider. Error copy
// stays in `sso-error-copy.ts`, mapped from a `HandledError` code.

export type SsoCopyKey = keyof typeof EN;
export type SsoCatalog = typeof EN;

const EN = {
  // Document titles and navigation.
  setupScreenTitle: "Single sign-on · {product}",
  idpScreenTitle: "Sign in with your company · {product}",
  navSingleSignOn: "Single sign-on",

  // Providers.
  providerOkta: "Okta",
  providerMicrosoftEntra: "Microsoft Entra ID",
  providerGoogleWorkspace: "Google Workspace",
  providerAuth0: "Auth0",
  providerOidc: "Other (OpenID Connect)",

  // Steps.
  stepConnectTitle: "Connect",
  stepConnectDescription: "Your identity provider",
  stepDomainTitle: "Verify domain",
  stepDomainDescription: "A DNS TXT record",
  stepTestTitle: "Test",
  stepTestDescription: "Sign in once yourself",
  stepLiveTitle: "Go live",
  stepLiveDescription: "Turn it on for everyone",
  never: "Never",
  roleMember: "Member",
  roleViewer: "Viewer",

  // Setup screen.
  breadcrumbSettings: "Settings",
  setupTitle: "Single sign-on",
  setupDescription:
    "Let your team sign in with your company's identity provider, and manage who gets in from one place.",
  stepperLabel: "Single sign-on setup",
  upgradeTitle: "Upgrade to use single sign-on",
  upgradeDescription: "Single sign-on is on the Enterprise plan.",
  seePlans: "See plans",
  identityProviderTitle: "Identity provider",
  remove: "Remove",
  labelProvider: "Provider",
  labelIssuer: "Issuer",
  labelClientId: "Client ID",
  labelStatus: "Status",
  labelConnected: "Connected",
  statusRequired: "Required",
  statusOn: "On",
  statusOff: "Off",
  removeProviderTitle: "Remove the identity provider?",
  removeProviderDescription:
    "Single sign-on turns off and everyone on your domains signs in with a password again. Their accounts stay.",

  // Connect panel.
  connectTitle: "Connect your identity provider",
  connectDescription: "Create an OpenID Connect app in your provider, then paste its details here.",
  redirectHint: "In your provider, set this as the app's sign-in redirect URI:",
  redirectUriLabel: "Redirect URI",
  providerLabel: "Provider",
  issuerUrlLabel: "Issuer URL",
  clientIdLabel: "Client ID",
  clientSecretLabel: "Client secret",
  connect: "Connect",

  // Domains panel.
  domainsTitle: "Email domains",
  domainsDescription: "Everyone whose email is on a verified domain signs in through your provider.",
  domainLabel: "Domain",
  addDomain: "Add domain",
  noDomainsHint: "Add the domain your team's email addresses use, like acme.com.",
  domainVerifiedOn: "Verified {date}",
  domainLastChecked: "Last checked {date}",
  domainAddRecordHint: "Add this TXT record at your DNS host, then check it.",
  domainStatusPending: "Waiting for DNS",
  domainStatusFailed: "Record not found",
  domainStatusVerified: "Verified",
  txtRecordNameLabel: "TXT record name",
  txtRecordValueLabel: "TXT record value",
  checkDns: "Check DNS",
  publishForMe: "Publish it for me (simulation)",
  domainJoinLabel: "Let people on a verified domain join",
  domainJoinOnDescription:
    "Anyone proven on {domains} joins this organization automatically, without an invite.",
  domainJoinVerifyFirst: "Verify a domain first.",
  domainJoinRoleLabel: "Role they join with",

  // Rollout panel.
  rolloutTitle: "Test and go live",
  rolloutDescription: "Nothing changes for your team until you turn it on.",
  testSignInTitle: "Test sign-in",
  testPassed: "Last test passed {date}.",
  testPrompt: "Sign in through {provider} once yourself, with an email on one of your domains.",
  testAgain: "Test again",
  testSignIn: "Test sign-in",
  yourVerifiedDomains: "your verified domains",
  liveLabel: "Single sign-on for your team",
  liveReadyDescription: 'People with an email on {domains} see "Continue with {provider}" when they sign in.',
  liveNotReadyDescription: "Verify a domain and pass a test sign-in to turn this on.",
  requireLabel: "Require single sign-on",
  requireDescription:
    "Passwords stop working on {domains}. Use it once everyone has signed in through {provider} at least once.",
  newPeopleTitle: "New people",
  newPeopleDescription: "When someone on your domain signs in for the first time.",
  joinCreate: "Create their account",
  joinRefuse: "Only existing accounts",

  // Toasts.
  stepFailed: "Could not finish that step",
  providerFallback: "Provider",
  providerConnected: "{provider} connected",
  connectedNext: "Next, prove you own your email domain.",
  domainRemoved: "{domain} removed",
  domainVerifiedTitle: "{domain} verified",
  verifiedNext: "Next, test the sign-in yourself.",
  recordNotFoundTitle: "Record not found yet",
  recordNotFoundDescription: "DNS changes can take a few minutes. Check again shortly.",
  recordPublishedTitle: "Record published (simulated)",
  recordPublishedDescription: "Now check the DNS.",
  ssoOnTitle: "Single sign-on is on",
  ssoOnDescription: "People on your domains now see it at sign-in.",
  ssoOffTitle: "Single sign-on is off",
  ssoOffDescription: "Everyone signs in with a password again.",
  ssoRequiredTitle: "Single sign-on required",
  ssoRequiredDescription: "Passwords no longer work on your domains.",
  passwordsAllowedTitle: "Passwords allowed again",
  domainJoinOnTitle: "Domain join is on",
  domainJoinOnToast: "Anyone proven on a verified domain joins automatically.",
  domainJoinOffTitle: "Domain join is off",
  providerRemovedTitle: "Identity provider removed",

  // The stand-in identity provider page.
  idpFallbackName: "Identity provider",
  idpSimulated: "Simulated",
  idpTitle: "Sign in to {provider}",
  idpTitleFallback: "Sign in",
  idpSubtitleTest: "Testing your connection. You stay signed in to {product} as you are.",
  idpSubtitle: "{product} asked your company's identity provider who you are.",
  workEmailLabel: "Work email",
  workEmailHint: "An address on {domains}.",
  yourCompanyDomain: "your company's domain",
  firstNameLabel: "First name",
  lastNameLabel: "Last name",
  continue: "Continue",
  idpNotice:
    "This page stands in for {provider}. In the simulation no password is asked and nothing leaves {product}.",
  signInFailed: "Could not sign in",
  firstNameRequired: "Enter your first name.",
  testPassedTitle: "Test sign-in passed",
  testPassedDescription: "You can turn single sign-on on now.",
} as const;

const ES: Translation<SsoCatalog> = {
  setupScreenTitle: "Inicio de sesión único · {product}",
  idpScreenTitle: "Inicia sesión con tu empresa · {product}",
  navSingleSignOn: "Inicio de sesión único",

  providerOkta: "Okta",
  providerMicrosoftEntra: "Microsoft Entra ID",
  providerGoogleWorkspace: "Google Workspace",
  providerAuth0: "Auth0",
  providerOidc: "Otro (OpenID Connect)",

  stepConnectTitle: "Conectar",
  stepConnectDescription: "Tu proveedor de identidad",
  stepDomainTitle: "Verificar dominio",
  stepDomainDescription: "Un registro DNS TXT",
  stepTestTitle: "Probar",
  stepTestDescription: "Inicia sesión tú una vez",
  stepLiveTitle: "Activar",
  stepLiveDescription: "Actívalo para todos",
  never: "Nunca",
  roleMember: "Miembro",
  roleViewer: "Visualizador",

  breadcrumbSettings: "Configuración",
  setupTitle: "Inicio de sesión único",
  setupDescription:
    "Deja que tu equipo inicie sesión con el proveedor de identidad de tu empresa y gestiona desde un solo lugar quién entra.",
  stepperLabel: "Configuración del inicio de sesión único",
  upgradeTitle: "Mejora tu plan para usar el inicio de sesión único",
  upgradeDescription: "El inicio de sesión único está en el plan Enterprise.",
  seePlans: "Ver planes",
  identityProviderTitle: "Proveedor de identidad",
  remove: "Quitar",
  labelProvider: "Proveedor",
  labelIssuer: "Emisor",
  labelClientId: "ID de cliente",
  labelStatus: "Estado",
  labelConnected: "Conectado",
  statusRequired: "Obligatorio",
  statusOn: "Activo",
  statusOff: "Inactivo",
  removeProviderTitle: "¿Quitar el proveedor de identidad?",
  removeProviderDescription:
    "El inicio de sesión único se desactiva y todos en tus dominios vuelven a iniciar sesión con contraseña. Sus cuentas se conservan.",

  connectTitle: "Conecta tu proveedor de identidad",
  connectDescription: "Crea una app OpenID Connect en tu proveedor y pega aquí sus datos.",
  redirectHint: "En tu proveedor, usa esto como URI de redirección de inicio de sesión de la app:",
  redirectUriLabel: "URI de redirección",
  providerLabel: "Proveedor",
  issuerUrlLabel: "URL del emisor",
  clientIdLabel: "ID de cliente",
  clientSecretLabel: "Secreto de cliente",
  connect: "Conectar",

  domainsTitle: "Dominios de correo",
  domainsDescription:
    "Todos los que tengan un correo en un dominio verificado inician sesión con tu proveedor.",
  domainLabel: "Dominio",
  addDomain: "Añadir dominio",
  noDomainsHint: "Añade el dominio que usan los correos de tu equipo, como acme.com.",
  domainVerifiedOn: "Verificado {date}",
  domainLastChecked: "Última comprobación {date}",
  domainAddRecordHint: "Añade este registro TXT en tu proveedor de DNS y luego comprueba.",
  domainStatusPending: "Esperando al DNS",
  domainStatusFailed: "Registro no encontrado",
  domainStatusVerified: "Verificado",
  txtRecordNameLabel: "Nombre del registro TXT",
  txtRecordValueLabel: "Valor del registro TXT",
  checkDns: "Comprobar DNS",
  publishForMe: "Publícalo por mí (simulación)",
  domainJoinLabel: "Permitir que se unan las personas de un dominio verificado",
  domainJoinOnDescription:
    "Cualquiera verificado en {domains} se une a esta organización automáticamente, sin invitación.",
  domainJoinVerifyFirst: "Verifica un dominio primero.",
  domainJoinRoleLabel: "Rol con el que se unen",

  rolloutTitle: "Probar y activar",
  rolloutDescription: "Nada cambia para tu equipo hasta que lo actives.",
  testSignInTitle: "Prueba de inicio de sesión",
  testPassed: "La última prueba fue exitosa el {date}.",
  testPrompt: "Inicia sesión una vez con {provider}, con un correo de uno de tus dominios.",
  testAgain: "Probar de nuevo",
  testSignIn: "Probar inicio de sesión",
  yourVerifiedDomains: "tus dominios verificados",
  liveLabel: "Inicio de sesión único para tu equipo",
  liveReadyDescription:
    'Quienes tengan un correo en {domains} verán "Continuar con {provider}" al iniciar sesión.',
  liveNotReadyDescription: "Verifica un dominio y pasa una prueba de inicio de sesión para activarlo.",
  requireLabel: "Exigir inicio de sesión único",
  requireDescription:
    "Las contraseñas dejan de funcionar en {domains}. Úsalo cuando todos hayan iniciado sesión con {provider} al menos una vez.",
  newPeopleTitle: "Personas nuevas",
  newPeopleDescription: "Cuando alguien de tu dominio inicia sesión por primera vez.",
  joinCreate: "Crear su cuenta",
  joinRefuse: "Solo cuentas existentes",

  stepFailed: "No se pudo completar ese paso",
  providerFallback: "Proveedor",
  providerConnected: "{provider} conectado",
  connectedNext: "Ahora demuestra que eres dueño de tu dominio de correo.",
  domainRemoved: "{domain} quitado",
  domainVerifiedTitle: "{domain} verificado",
  verifiedNext: "Ahora prueba tú el inicio de sesión.",
  recordNotFoundTitle: "Aún no se encuentra el registro",
  recordNotFoundDescription: "Los cambios de DNS pueden tardar unos minutos. Vuelve a comprobar en un rato.",
  recordPublishedTitle: "Registro publicado (simulado)",
  recordPublishedDescription: "Ahora comprueba el DNS.",
  ssoOnTitle: "El inicio de sesión único está activo",
  ssoOnDescription: "Las personas de tus dominios ya lo ven al iniciar sesión.",
  ssoOffTitle: "El inicio de sesión único está inactivo",
  ssoOffDescription: "Todos vuelven a iniciar sesión con contraseña.",
  ssoRequiredTitle: "Inicio de sesión único obligatorio",
  ssoRequiredDescription: "Las contraseñas ya no funcionan en tus dominios.",
  passwordsAllowedTitle: "Contraseñas permitidas de nuevo",
  domainJoinOnTitle: "La unión por dominio está activa",
  domainJoinOnToast: "Cualquiera verificado en un dominio verificado se une automáticamente.",
  domainJoinOffTitle: "La unión por dominio está inactiva",
  providerRemovedTitle: "Proveedor de identidad quitado",

  idpFallbackName: "Proveedor de identidad",
  idpSimulated: "Simulado",
  idpTitle: "Inicia sesión en {provider}",
  idpTitleFallback: "Inicia sesión",
  idpSubtitleTest: "Probando tu conexión. Sigues con la sesión de {product} abierta como estás.",
  idpSubtitle: "{product} le preguntó al proveedor de identidad de tu empresa quién eres.",
  workEmailLabel: "Correo de trabajo",
  workEmailHint: "Una dirección de {domains}.",
  yourCompanyDomain: "el dominio de tu empresa",
  firstNameLabel: "Nombre",
  lastNameLabel: "Apellido",
  continue: "Continuar",
  idpNotice:
    "Esta página hace las veces de {provider}. En la simulación no se pide contraseña y nada sale de {product}.",
  signInFailed: "No se pudo iniciar sesión",
  firstNameRequired: "Escribe tu nombre.",
  testPassedTitle: "Prueba de inicio de sesión exitosa",
  testPassedDescription: "Ya puedes activar el inicio de sesión único.",
};

export const SSO_CATALOGS: Catalogs<SsoCatalog> = { en: EN, es: ES };

type SsoTranslate = Translate<SsoCatalog>;

export const PROVIDER_OPTIONS: ReadonlyArray<{
  value: Provider;
  labelKey: PlainKey<SsoCatalog>;
  issuer: string;
}> = [
  { value: "okta", labelKey: "providerOkta", issuer: "https://your-company.okta.com" },
  {
    value: "microsoft-entra",
    labelKey: "providerMicrosoftEntra",
    issuer: "https://login.microsoftonline.com/<tenant-id>/v2.0",
  },
  { value: "google-workspace", labelKey: "providerGoogleWorkspace", issuer: "https://accounts.google.com" },
  { value: "auth0", labelKey: "providerAuth0", issuer: "https://your-company.eu.auth0.com" },
  { value: "oidc", labelKey: "providerOidc", issuer: "https://sso.your-company.com" },
];

/** The provider picker's options, labelled in the active language. */
export function providerOptions(t: SsoTranslate): readonly SelectOption[] {
  return PROVIDER_OPTIONS.map((option) => ({ value: option.value, label: t(option.labelKey) }));
}

export type SetupStep = "connect" | "domain" | "test" | "live";

/** Where the admin is: the first step not yet done. */
export function currentStep(connection: SsoConnection | null): SetupStep {
  if (!connection) return "connect";
  if (!connection.domains.some((domain) => domain.status === "verified")) return "domain";
  if (!connection.testedAt) return "test";
  return "live";
}

export const STEPS: ReadonlyArray<{
  id: SetupStep;
  titleKey: PlainKey<SsoCatalog>;
  descriptionKey: PlainKey<SsoCatalog>;
}> = [
  { id: "connect", titleKey: "stepConnectTitle", descriptionKey: "stepConnectDescription" },
  { id: "domain", titleKey: "stepDomainTitle", descriptionKey: "stepDomainDescription" },
  { id: "test", titleKey: "stepTestTitle", descriptionKey: "stepTestDescription" },
  { id: "live", titleKey: "stepLiveTitle", descriptionKey: "stepLiveDescription" },
];

/** A moment as the active language writes it, or the word for "never". */
export function shortDateTime(iso: string | null, reading: { t: SsoTranslate; format: Formats }): string {
  return iso ? reading.format.dateTime(iso, { dateStyle: "medium", timeStyle: "short" }) : reading.t("never");
}

export const DOMAIN_STATUS: Readonly<
  Record<DomainStatus, { tone: "neutral" | "warning" | "success"; labelKey: PlainKey<SsoCatalog> }>
> = {
  pending: { tone: "neutral", labelKey: "domainStatusPending" },
  failed: { tone: "warning", labelKey: "domainStatusFailed" },
  verified: { tone: "success", labelKey: "domainStatusVerified" },
};

/** The role domain join may hand a new joiner (S1.07): never admin, restated here (a contract
 * may depend only on zod and @amtana/module), the same shape access-requests' own role
 * picker already restates for the same reason. */
export function domainJoinRoleOptions(t: SsoTranslate): readonly SelectOption[] {
  return [
    { value: "member", label: t("roleMember") },
    { value: "viewer", label: t("roleViewer") },
  ];
}
